import { SolisecoModel } from "./Device";

export const CONFIG_NUMBER_RE = /Config\s+(\d+)/i;

export const MODEL_ALLOWED_CONFIG_NUMBERS: Record<SolisecoModel, number[]> = {
  "1P_Intraheat": [0, 1, 4, 5, 6],
  "1P_Extraheat": [0, 1, 2, 3, 4, 5, 6, 7, 8],
  "1P_SG_Ready": [0, 61, 62, 63, 64, 65, 66, 67, 68],
  "3P_Extraheat": [0, 31, 32, 33, 34, 35, 36, 37, 38],
};

export const MODEL_DEFAULT_CONFIG_NUMBER: Record<SolisecoModel, number> = {
  "1P_Intraheat": 0,
  "1P_Extraheat": 0,
  "1P_SG_Ready": 0,
  "3P_Extraheat": 0,
};

export const COMMISSIONING_TIP_LANGUAGES = ["en", "fr", "de", "nl"] as const;
export type CommissioningTipLanguage = (typeof COMMISSIONING_TIP_LANGUAGES)[number];

export type ParameterConfigurationOption = {
  id: number;
  name: string;
  commissioning_tip_en?: string;
  commissioning_tip_fr?: string;
  commissioning_tip_de?: string;
  commissioning_tip_nl?: string;
  /** @deprecated use commissioning_tip_en / localized fields */
  commissioning_tip?: string;
  config_number?: number | null;
};

export function normalizeAppLanguage(language?: string | null): CommissioningTipLanguage {
  const lang = (language || "en").split("-")[0].toLowerCase();
  if ((COMMISSIONING_TIP_LANGUAGES as readonly string[]).includes(lang)) {
    return lang as CommissioningTipLanguage;
  }
  return "en";
}

export function getCommissioningTipForLanguage(
  config: ParameterConfigurationOption | null | undefined,
  language?: string | null
): string {
  if (!config) return "";
  const lang = normalizeAppLanguage(language);
  const byLang: Record<CommissioningTipLanguage, string> = {
    en: (config.commissioning_tip_en || config.commissioning_tip || "").trim(),
    fr: (config.commissioning_tip_fr || "").trim(),
    de: (config.commissioning_tip_de || "").trim(),
    nl: (config.commissioning_tip_nl || "").trim(),
  };
  return byLang[lang] || byLang.en || "";
}

export const CONFIG_2_NUMBERS = [2];
export const CIRCULATOR_POWER_PERCENT_MAX = 50;
export const CIRCULATOR_POWER_PERCENT_MAX_CONFIG_2 = 200;

export function circulatorPowerPercentMax(configName?: string | null): number {
  const number = parseConfigNumber(configName);
  if (number != null && CONFIG_2_NUMBERS.includes(number)) {
    return CIRCULATOR_POWER_PERCENT_MAX_CONFIG_2;
  }
  return CIRCULATOR_POWER_PERCENT_MAX;
}

export function parseConfigNumber(name?: string | null): number | null {
  if (!name) return null;
  const match = name.match(CONFIG_NUMBER_RE);
  if (!match) return null;
  return parseInt(match[1], 10);
}

export function isConfigAllowedForModel(
  configName: string | undefined,
  solisecoModel: string | undefined
): boolean {
  const allowed = MODEL_ALLOWED_CONFIG_NUMBERS[solisecoModel as SolisecoModel];
  if (!allowed) return false;
  const number = parseConfigNumber(configName);
  return number != null && allowed.includes(number);
}

export function sortConfigsByNumber<T extends ParameterConfigurationOption>(
  configs: T[]
): T[] {
  return [...configs].sort((a, b) => {
    const aNumber = a.config_number ?? parseConfigNumber(a.name);
    const bNumber = b.config_number ?? parseConfigNumber(b.name);
    const aKey = aNumber == null ? Number.POSITIVE_INFINITY : aNumber;
    const bKey = bNumber == null ? Number.POSITIVE_INFINITY : bNumber;
    if (aKey !== bKey) return aKey - bKey;
    return a.name.localeCompare(b.name);
  });
}

export function filterConfigsForModel<T extends ParameterConfigurationOption>(
  configs: T[],
  solisecoModel: string | undefined,
  currentConfigId?: number | null
): T[] {
  const allowed = MODEL_ALLOWED_CONFIG_NUMBERS[solisecoModel as SolisecoModel];
  if (!allowed) return [];
  const filtered = configs.filter((config) => {
    const number = config.config_number ?? parseConfigNumber(config.name);
    return number != null && allowed.includes(number);
  });
  if (currentConfigId && !filtered.some((config) => config.id === currentConfigId)) {
    const current = configs.find((config) => config.id === currentConfigId);
    if (current) return sortConfigsByNumber([current, ...filtered]);
  }
  return sortConfigsByNumber(filtered);
}

/** Ensure the device's current config is present after an API allow-list fetch. */
export function mergeCurrentConfig<T extends ParameterConfigurationOption>(
  configs: T[],
  current?: T | null
): T[] {
  if (!current?.id) return configs;
  if (configs.some((config) => config.id === current.id)) return configs;
  return [current, ...configs];
}

export function getDefaultConfigId<T extends ParameterConfigurationOption>(
  configs: T[],
  solisecoModel: string | undefined,
  currentConfigId?: number | null
): number | null {
  const filtered = filterConfigsForModel(configs, solisecoModel);
  const preferredNumber = MODEL_DEFAULT_CONFIG_NUMBER[solisecoModel as SolisecoModel];
  const preferred = filtered.find((config) => {
    const number = config.config_number ?? parseConfigNumber(config.name);
    return number === preferredNumber;
  });
  if (preferred) {
    return preferred.id;
  }
  if (currentConfigId && filtered.some((config) => config.id === currentConfigId)) {
    return currentConfigId;
  }
  return filtered[0]?.id ?? null;
}

export function formatConfigOptionLabel(config: ParameterConfigurationOption): string {
  return config.name;
}
